<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import TicketCategoriesAPI from 'dashboard/api/ticketCategories';

const props = defineProps({
  categoryId: { type: [Number, String], default: null },
  subcategoryId: { type: [Number, String], default: null },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits([
  'update:categoryId',
  'update:subcategoryId',
  'change',
]);

const { t } = useI18n();

const categories = ref([]);
const loading = ref(true);
const selectedCategory = ref(props.categoryId);
const selectedSubcategory = ref(props.subcategoryId);

const subcategories = computed(() => {
  if (!selectedCategory.value) return [];
  const cat = categories.value.find(
    c => c.id === Number(selectedCategory.value)
  );
  return cat?.ticket_subcategories || [];
});

const selectedSubcategoryData = computed(() => {
  return subcategories.value.find(
    s => s.id === Number(selectedSubcategory.value)
  );
});

const priorityLabels = ['', 'Baja', 'Media', 'Alta', 'Urgente'];

const fetchCategories = async () => {
  try {
    loading.value = true;
    const response = await TicketCategoriesAPI.get({ active_only: 'true' });
    categories.value = response.data;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error loading categories:', error);
  } finally {
    loading.value = false;
  }
};

watch(selectedCategory, newVal => {
  selectedSubcategory.value = null;
  emit('update:categoryId', newVal);
  emit('update:subcategoryId', null);
  emit('change', { categoryId: newVal, subcategoryId: null });
});

watch(selectedSubcategory, newVal => {
  emit('update:subcategoryId', newVal);
  emit('change', {
    categoryId: selectedCategory.value,
    subcategoryId: newVal,
    subcategoryData: selectedSubcategoryData.value,
  });
});

// Sync from props
watch(
  () => props.categoryId,
  val => {
    selectedCategory.value = val;
  }
);
watch(
  () => props.subcategoryId,
  val => {
    selectedSubcategory.value = val;
  }
);

onMounted(fetchCategories);

defineExpose({
  selectedSubcategoryData,
});
</script>

<template>
  <div class="flex flex-col gap-2">
    <!-- Category & Subcategory Row -->
    <div class="grid grid-cols-2 gap-3">
      <!-- Category -->
      <div class="flex flex-col gap-1">
        <label class="text-xs font-medium text-n-slate-11">
          {{ t('TICKET_CATEGORIES.SELECTOR.CATEGORY') }} *
        </label>
        <select
          v-model="selectedCategory"
          :disabled="disabled || loading"
          class="w-full px-2 py-1.5 text-sm rounded-md bg-n-alpha-1 border border-n-weak disabled:opacity-50 focus:border-woot-500 focus:outline-none"
          required
        >
          <option :value="null">
            {{ loading ? '...' : t('TICKET_CATEGORIES.SELECTOR.SELECT_CATEGORY') }}
          </option>
          <option v-for="cat in categories" :key="cat.id" :value="cat.id">
            {{ cat.name }}
          </option>
        </select>
      </div>

      <!-- Subcategory -->
      <div class="flex flex-col gap-1">
        <label class="text-xs font-medium text-n-slate-11">
          {{ t('TICKET_CATEGORIES.SELECTOR.SUBCATEGORY') }} *
        </label>
        <select
          v-model="selectedSubcategory"
          :disabled="disabled || !selectedCategory || subcategories.length === 0"
          class="w-full px-2 py-1.5 text-sm rounded-md bg-n-alpha-1 border border-n-weak disabled:opacity-50 focus:border-woot-500 focus:outline-none"
          required
        >
          <option :value="null">
            {{ !selectedCategory ? '--' : t('TICKET_CATEGORIES.SELECTOR.SELECT_SUBCATEGORY') }}
          </option>
          <option v-for="sub in subcategories" :key="sub.id" :value="sub.id">
            {{ sub.name }}
          </option>
        </select>
      </div>
    </div>

    <!-- Selected subcategory info (compact) -->
    <div
      v-if="selectedSubcategoryData"
      class="text-xs text-n-slate-11 flex items-center gap-2 flex-wrap"
    >
      <span
        class="px-1.5 py-0.5 rounded text-xs"
        :class="{
          'bg-g-100 text-g-800': selectedSubcategoryData.default_priority === 1,
          'bg-b-100 text-b-800': selectedSubcategoryData.default_priority === 2,
          'bg-y-100 text-y-800': selectedSubcategoryData.default_priority === 3,
          'bg-r-100 text-r-800': selectedSubcategoryData.default_priority === 4,
        }"
      >
        {{ priorityLabels[selectedSubcategoryData.default_priority] }}
      </span>
      <span v-if="selectedSubcategoryData.requires_contract" class="flex items-center gap-0.5">
        <fluent-icon icon="document" size="12" />
        Contrato
      </span>
      <span v-if="selectedSubcategoryData.requires_location" class="flex items-center gap-0.5">
        <fluent-icon icon="location" size="12" />
        Ubicacion
      </span>
      <span v-if="selectedSubcategoryData.sla_hours" class="flex items-center gap-0.5">
        <fluent-icon icon="clock" size="12" />
        {{ selectedSubcategoryData.sla_hours }}h
      </span>
    </div>
  </div>
</template>
